import {ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {BreakpointObserver, Breakpoints, BreakpointState} from '@angular/cdk/layout';
import {MatDialog} from '@angular/material/dialog';
import {MatDrawer, MatDrawerMode} from '@angular/material/sidenav';
import {BehaviorSubject, combineLatest, distinctUntilChanged, Observable, sample, shareReplay, Subject, switchMap} from 'rxjs';
import {map} from 'rxjs/operators';
import {CatalogFacetsComponent} from './components/catalog-facets/catalog-facets.component';
import {CatalogBrowseService} from './catalog-browse.service';
import {SearchFacets} from './mock-facets';
import {CatalogFacets, CatalogQuery, CatalogRecord, CatalogRecordService, CatalogSearchResponse, Page, Sort, SubmitRequestComponent} from '@raven';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PatronSelectors} from '@store/store.selectors';
import {CatalogRecordSearchTarget} from '../../../model/catalog-search/catalog-record-search-target';

@Component({
  selector: 'rn-catalog-browse',
  templateUrl: './catalog-browse.component.html',
  styleUrl: './catalog-browse.component.scss',
  providers: [CatalogBrowseService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class CatalogBrowseComponent implements OnInit {
  public readonly patronStatusSelectors = PatronSelectors.AccountStatus;
  private readonly destroyRef = inject(DestroyRef);
  @ViewChildren(CatalogFacetsComponent)
  facetComponents: QueryList<CatalogFacetsComponent>;
  loadingSubject = new BehaviorSubject<boolean>(false);

  items: Array<CatalogRecord> = [];
  searchForm: FormGroup;
  searchTarget: CatalogRecordSearchTarget = 'KEYWORD';
  defaultDisplayMode = 'grid'; //DisplayMode.Grid;
  displayMode: string = this.defaultDisplayMode; //DisplayMode;
  facets = new SearchFacets().facets;

  showExpandedSearch = false;
  showExpandedSearchWarning = true;
  isMobile = false;
  filtersOpened = true;
  facetDrawerMode: MatDrawerMode = 'side';
  @ViewChild(MatDrawer) facetDrawer: MatDrawer;
  sortSelectValue = '';

  searchButtonSubject = new Subject<boolean>();
  searchResponse$: Observable<CatalogSearchResponse>;

  pageSubject = this.catalogBrowseService.pageSubject
  page$ = this.catalogBrowseService.page$;
  querySubject = this.catalogBrowseService.querySubject;
  query$ = this.catalogBrowseService.query$;
  // filterSubject = this.catalogBrowseService.filterSubject;
  filter$ = this.catalogBrowseService.filter$;
  sortSubject = this.catalogBrowseService.sortSubject;
  sort$ = this.catalogBrowseService.sort$;


  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private catalogRecordService: CatalogRecordService,
    public catalogBrowseService: CatalogBrowseService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog
  ) {
  }

  ngOnInit(): void {
    this.loadingSubject.next(true);
    this.searchForm = this.fb.group({
      searchTarget: ['KEYWORD' as CatalogRecordSearchTarget, {validators: []}],
      searchTerm: ['', {validators: []}],
    });

    this.searchForm.valueChanges
      .pipe(
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        shareReplay({bufferSize: 1, refCount: false}),
        sample(this.searchButtonSubject),
        map(formValue => {
          return {query: formValue['searchTerm'], searchTarget: formValue['searchTarget']} as CatalogQuery;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(query => {
        this.catalogBrowseService.updateQuery(query);
      });

    const pageQueryFilterSort$ = combineLatest([this.page$, this.query$, this.filter$, this.sort$]).pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    );

    this.searchResponse$ = pageQueryFilterSort$.pipe(
      switchMap(([page, query, filters, sort]) => {
        return this.catalogRecordService.elasticSearch(page, query, filters, sort);
      }),
    );

    pageQueryFilterSort$.pipe(
      map(([page, query, filters, sort]) => this.serializeToUrlParams(page, query, filters, sort)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    this.route.queryParams.pipe(
      map(params => this.setupFromUrlParams(params)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();

    this.breakpointObserver
      .observe([Breakpoints.XSmall])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((breakpointState: BreakpointState) => {
        if (breakpointState.matches) {
          this.isMobile = true;
          this.filtersOpened = false;
          this.facetDrawerMode = 'over';
        } else {
          this.isMobile = false;
          this.filtersOpened = true;
          this.facetDrawerMode = 'side';
        }
      });
  }

  setupFromUrlParams(params: Params): void {
    const urlPage = parseInt(params['page']);
    const page = params['page'] && !isNaN(urlPage) && urlPage >= 0 ? urlPage : 0;
    const urlLimit = parseInt(params['limit']);
    const limit = params['limit'] && !isNaN(urlLimit) && urlLimit > 0 ? urlLimit : 25;
    const pageState = {page, limit} as Page;
    this.pageSubject.next(pageState);

    const query = params['query'];
    const target = params['searchTarget'];
    if (query) {
      this.searchForm.get('searchTerm').setValue(query);
    }
    if (target) {
      this.searchForm.get('searchTarget').setValue(target);
      this.searchTarget = target;
    }
    const queryState = {query: this.searchForm.get('searchTerm').value, searchTarget: this.searchForm.get('searchTarget').value} as CatalogQuery;
    this.querySubject.next(queryState);

    const filtersObject = params['filters'] ?? JSON.stringify({});
    let catalogFilters;
    try {
      catalogFilters = JSON.parse(filtersObject);
    } catch (e) {
      catalogFilters = {};
    }

    const filterLinkType = params['filterLinkType'];
    const filterLinkValue = params['filterLinkValue'];
    if (filterLinkType && filterLinkValue) {
      catalogFilters = CatalogFacets.fromLinkParams(filterLinkType, filterLinkValue);
    }

    this.catalogBrowseService.updateFilters(catalogFilters);

    const sortBy = params['sortBy'];
    const direction = params['direction'];
    if (sortBy && direction) {
      this.sortSelectValue = sortBy + '-' + direction;
    }
    this.sortSubject.next({column: sortBy, direction});
  }

  submitForm(): void {
    this.searchButtonSubject.next(true);
  }

  setPageNumber(pageNumber: number): void {
    this.catalogBrowseService.setPage(pageNumber);
    window.scrollTo(0, 0);
  }

  getPageCount(totalResults: number, limit: number): number {
    return Math.ceil(totalResults / limit);
  }

  getPageNumbersForControl(currentPage: number, totalResults: number, limit: number): Array<number> {
    const pageCount = this.getPageCount(totalResults, limit);
    const firstPage = currentPage - 4 < 1 ? 1 : currentPage - 4;
    const lastPage = currentPage + 4 > pageCount ? pageCount : currentPage + 4;
    const pageNumbersForControl = [];
    for (let i = firstPage; i <= lastPage; i++) {
      pageNumbersForControl.push(i);
    }
    return pageNumbersForControl;
  }

  serializeToUrlParams(page: Page, query: CatalogQuery, filters: CatalogFacets, sort: Sort) {
    // used to limit filter properties to those needed by API / displayed in the URL
    const fieldsToKeep = ['subjects.keyword', 'genres.keyword', 'materialTypeIds', 'branchIds', 'collectionIds', 'literaryForm', 'targetAudience', 'author.keyword', 'publicationDate.keyword', 'name', 'values', 'filterValue'];

    const newParams = {
      query: query.query,
      searchTarget: query.searchTarget,
      page: page.page,
      limit: page.limit,
      filters: JSON.stringify(filters, fieldsToKeep),
      sortBy: sort?.column,
      direction: sort?.direction,
      filterLinkType: undefined,
      filterLinkValue: undefined,
    }
    this.router.navigate([this.route.snapshot.url[0].path], {
      queryParams: newParams,
      queryParamsHandling: 'merge',
    });
  }

  setDisplayMode(mode): void {
    this.displayMode = mode;
  }

  setPageSize(length): void {
    this.catalogBrowseService.setPageAndLimit(0, length);
  }

  openItemRequestModal(): void {
    this.dialog.open(SubmitRequestComponent);
  }

  hasFilters(response: CatalogSearchResponse): boolean {
    if (!response) {
      return false;
    }
    for (const facetKey of Object.keys(response.facets)) {
      if (response.facets[facetKey].values && response.facets[facetKey].values.filter(facetValue => facetValue.selected).length > 0) {
        return true;
      }
    }
    return false;
  }

  sortChanged(event) {
    const sortObject = this.sortObjectFromSelectValue(event.target.value);
    this.catalogBrowseService.updateSort(sortObject);
  }

  sortObjectFromSelectValue(sortValue: string): Sort {
    const splitSortValue = sortValue.split('-');
    if (splitSortValue.length !== 2) {
      return null;
    }
    return {
      column: splitSortValue[0],
      direction: splitSortValue[1],
    } as Sort;
  }
}
